// Гости: база лояльности. Телефон — карта, пороги — настройка владельца.
import { useEffect, useState } from 'react';
import { useAskSheet, useNote } from './ui/ask';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GuestsList, HallMap, LocationsList, LoyaltySettings, TopGuests } from '@dastarhan/contracts';
import { formatMoney, formatPhoneKz } from '@dastarhan/core';
import { downloadCsv, tg } from './csv';

const API = '/api/v1';

export function GuestsScreen({ token, isOwner }: { token: string; isOwner: boolean }) {
  const { ask, sheet } = useAskSheet();
  const { note, noteView } = useNote();
  const qc = useQueryClient();
  const h = { Authorization: `Bearer ${token}` };
  const jh = { ...h, 'Content-Type': 'application/json' };

  const top = useQuery({
    queryKey: ['guests-top'],
    queryFn: async () => TopGuests.parse(await (await fetch(`${API}/guests/top`, { headers: h })).json()),
  });
  const guests = useQuery({
    queryKey: ['guests'],
    queryFn: async () => GuestsList.parse(await (await fetch(`${API}/guests`, { headers: h })).json()),
  });
  const loyalty = useQuery({
    queryKey: ['loyalty'],
    queryFn: async () => LoyaltySettings.parse(await (await fetch(`${API}/guests/loyalty`, { headers: h })).json()),
  });

  const [tiers, setTiers] = useState<{ from: string; percent: string }[]>([]);
  useEffect(() => {
    if (loyalty.data) {
      setTiers(loyalty.data.map((t) => ({ from: String(t.from / 100), percent: String(t.percent) })));
    }
  }, [loyalty.data]);

  const saveLoyalty = useMutation({
    mutationFn: (body: { from: number; percent: number }[]) =>
      fetch(`${API}/guests/loyalty`, { method: 'PUT', headers: jh, body: JSON.stringify(body) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['loyalty'] });
      void qc.invalidateQueries({ queryKey: ['guests'] });
    },
  });
  const patchName = useMutation({
    mutationFn: (b: { id: string; name: string }) =>
      fetch(`${API}/guests/${b.id}`, { method: 'PATCH', headers: jh, body: JSON.stringify({ name: b.name }) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['guests'] }),
  });

  const [q, setQ] = useState('');
  const [bookFor, setBookFor] = useState<{ guestId: string; name: string | null; phone: string } | null>(null);
  const [bk, setBk] = useState({ tableId: '', date: '', time: '19:00', durationMin: '120', guests: '2' });
  const [bkDone, setBkDone] = useState(false);

  const locations = useQuery({
    queryKey: ['locations'],
    enabled: !!bookFor,
    queryFn: async () => LocationsList.parse(await (await fetch(`${API}/menu/locations`, { headers: h })).json()),
  });
  const loc = locations.data?.[0]?.id ?? null;
  const halls = useQuery({
    queryKey: ['halls-map', loc],
    enabled: !!bookFor && !!loc,
    queryFn: async () => HallMap.parse(await (await fetch(`${API}/hall/map?locationId=${loc}`, { headers: h })).json()),
  });
  const tables = (halls.data ?? []).flatMap((hl) => hl.tables.map((t) => ({ ...t, hall: hl.name })));
  const createResv = useMutation({
    mutationFn: (body: object) =>
      fetch(`${API}/reservations`, { method: 'POST', headers: jh, body: JSON.stringify(body) })
        .then(async (r) => { if (!r.ok) throw new Error((await r.json()).code ?? 'ERR'); }),
    onSuccess: () => setBkDone(true),
  });

  if (guests.isPending || loyalty.isPending) return <p className="empty">Загружаем гостей…</p>;
  if (guests.isError || !guests.data) return <p className="empty">Не удалось загрузить. Обновите страницу.</p>;

  const parsedTiers = tiers
    .map((t) => ({
      from: Math.round(Number(t.from.replace(',', '.')) * 100),
      percent: Math.trunc(Number(t.percent)),
    }))
    .filter((t) => Number.isFinite(t.from) && t.from >= 0 && t.percent >= 1 && t.percent <= 50);
  const tiersValid = parsedTiers.length === tiers.filter((t) => t.from.trim() !== '' || t.percent.trim() !== '').length;

  const digits = q.replace(/\D/g, '');
  const rows = guests.data.rows.filter((g) =>
    (digits === '' || g.phone.includes(digits)) &&
    (q.trim() === '' || digits !== '' || (g.name ?? '').toLowerCase().includes(q.trim().toLowerCase())),
  );

  return (
    <div className="guests">
      {(() => {
        const from = Date.now() - 30 * 86400_000;
        const fresh = (guests.data?.rows ?? []).filter((x) => x.firstSeen && new Date(x.firstSeen).getTime() >= from).length;
        return fresh > 0
          ? <p className="hint-inline" style={{ margin: '0 0 6px' }}>🌟 Новых гостей за 30 дней: {fresh}</p>
          : null;
      })()}
      {(top.data?.rows.length ?? 0) > 0 && (
        <p className="hint-inline" style={{ margin: '0 0 10px' }}>
          🏆 Топ месяца: {top.data!.rows.map((x) => `${x.name ?? x.phone} — ${formatMoney(x.sum)} (${x.checks} чек.)`).join(' · ')}
      {' '}<button className="memo-x btn-inline" onClick={() =>
        downloadCsv('топ_гостей_30.csv', [
          ['Гость', 'Телефон', 'Сумма, ₸', 'Чеков'],
          ...top.data!.rows.map((x) => [x.name ?? '', formatPhoneKz(x.phone), tg(x.sum), x.checks]),
        ])}>CSV</button>
        </p>
      )}
      {isOwner && <ReferralBox token={token} />}
      {isOwner && <FeedbackBox token={token} />}
      <section className="menu-cat">
        <header>
          <h2>Пороги лояльности</h2>
          <span className="rule-tag">{loyalty.data?.length ? 'включена' : 'выключена'}</span>
        </header>
        <div className="promo-form">
          <p className="hint-inline" style={{ margin: 0 }}>
            Скидка гостя — по последнему достигнутому порогу его покупок. Пустой список — лояльность выключена.
          </p>
          {tiers.map((t, i) => (
            <div key={i} className="row">
              <label>От суммы, ₸
                <input inputMode="decimal" value={t.from} style={{ width: 130 }} disabled={!isOwner}
                  onChange={(e) => setTiers(tiers.map((x, j) => j === i ? { ...x, from: e.target.value } : x))} />
              </label>
              <label>Скидка, %
                <input inputMode="numeric" value={t.percent} style={{ width: 70 }} disabled={!isOwner}
                  onChange={(e) => setTiers(tiers.map((x, j) => j === i ? { ...x, percent: e.target.value } : x))} />
              </label>
              {isOwner && (
                <button className="btn small" type="button" aria-label="Убрать порог"
                  onClick={() => setTiers(tiers.filter((_, j) => j !== i))}>✕</button>
              )}
            </div>
          ))}
          {isOwner && (
            <div className="row">
              <button className="btn small" type="button" disabled={tiers.length >= 5}
                onClick={() => setTiers([...tiers, { from: '', percent: '' }])}>+ Порог</button>
              <button className="btn accent small" type="button"
                disabled={!tiersValid || saveLoyalty.isPending}
                onClick={() => saveLoyalty.mutate(parsedTiers)}>
                Сохранить пороги
              </button>
            </div>
          )}
          {!isOwner && <p className="hint-inline" style={{ margin: 0 }}>Пороги меняет владелец.</p>}
        </div>
      </section>

      <div className="menu-toolbar" style={{ marginTop: 16 }}>
        <input placeholder="Поиск: телефон или имя…" value={q} onChange={(e) => setQ(e.target.value)}
          style={{ minWidth: 260 }} />
        <button className="btn" title="Не заходили 30+ дней — тёплая база для рассылки"
          onClick={() => {
            const cutoff = Date.now() - 30 * 86400_000;
            const rows = guests.data!.rows.filter((x) =>
              x.lastVisit && new Date(x.lastVisit).getTime() < cutoff);
            if (rows.length === 0) { note('Спящих нет — все гости были недавно'); return; }
            downloadCsv('гости_спящие_30.csv', [
              ['Имя', 'Телефон', 'Визитов', 'Последний визит'],
              ...rows.map((x) => [x.name ?? '', formatPhoneKz(x.phone), x.visits,
                new Date(x.lastVisit!).toLocaleDateString('ru-RU')]),
            ]);
          }}>Спящие 30+</button>
        <button className="btn" disabled={rows.length === 0} onClick={() => {
          downloadCsv(`гости_${new Date().toISOString().slice(0, 10)}.csv`, [
            ['Телефон', 'Имя', 'Визитов', 'Куплено, ₸', 'Скидка, %', 'Последний визит'],
            ...rows.map((g) => [
              formatPhoneKz(g.phone), g.name ?? '', g.visits, tg(g.totalSpent), g.percent,
              g.lastVisit ? new Date(g.lastVisit).toLocaleDateString('ru-RU') : '',
            ]),
          ]);
        }}>Скачать CSV</button>
      </div>

      {rows.length === 0 && (
        <p className="empty">Гостей пока нет. Первый появится, когда кассир привяжет телефон к чеку на экране оплаты.</p>
      )}
      {rows.length > 0 && (
        <div className="rep-table guests-table">
          <div className="rep-row head">
            <span>Гость</span><span className="right">Визитов</span>
            <span className="right">Куплено</span><span className="right">Скидка</span>
            <span /><span className="right">Был</span>
          </div>
          {rows.map((g) => (
            <div key={g.guestId} className="rep-row">
              <span>
                <button className="name-btn" title="Задать имя"
                  onClick={() => void (async () => {
                    const a = await ask({
                      title: 'Имя гостя',
                      sub: 'Официант увидит его при брони и на кассе — так гостя узнают по имени.',
                      effects: [['Телефон', formatPhoneKz(g.phone)], ['Визитов', String(g.visits)]],
                      value: {
                        label: 'Как зовут',
                        initial: g.name ?? '',
                        placeholder: 'Айгүл',
                        hint: 'можно с фамилией, если тёзок много',
                      },
                      confirmLabel: 'Сохранить',
                    });
                    if (a) patchName.mutate({ id: g.guestId, name: a.value });
                  })()}>
                  <b>{g.name ?? 'Без имени'}</b>
                </button>
                <small className="hint-inline"> {formatPhoneKz(g.phone)}</small>
              </span>
              <span className="right">{g.visits}
                {(() => {
                  // ритм гостя: срок базы на число промежутков
                  if (g.visits < 3 || !g.firstSeen || !g.lastVisit) return null;
                  const days = (new Date(g.lastVisit).getTime() - new Date(g.firstSeen).getTime())
                    / 86400_000 / (g.visits - 1);
                  return days >= 1
                    ? <small className="hint-inline"> ≈раз в {Math.round(days)} дн.</small>
                    : null;
                })()}
              </span>
              <span className="money right">{formatMoney(g.totalSpent)}</span>
              <span className="right">
                {g.visits > 0 && (
                  <small className="hint-inline">
                    ср. {formatMoney(Math.round(g.totalSpent / g.visits))}
                  </small>
                )}
              </span>
              <span className="right">{g.percent > 0 ? `−${g.percent}%` : '—'}</span>
              <button className="btn small" onClick={() => {
                const d = new Date();
                setBk({
                  tableId: '',
                  date: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
                  time: '19:00', durationMin: '120', guests: '2',
                });
                setBkDone(false);
                createResv.reset();
                setBookFor({ guestId: g.guestId, name: g.name, phone: g.phone });
              }}>Бронь</button>
              <span className="right hint-inline">
                {g.lastVisit
                  ? new Date(g.lastVisit).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })
                  : '—'}
              </span>
            </div>
          ))}
        </div>
      )}
      {bookFor && (
        <div className="dialog-backdrop" role="dialog" aria-modal="true" onClick={() => setBookFor(null)}>
          <div className="office-dialog" onClick={(e) => e.stopPropagation()}>
            {bkDone ? (
              <>
                <h2>Бронь создана</h2>
                <p className="dlg-line">
                  {bookFor.name ?? formatPhoneKz(bookFor.phone)} получит SMS, если канал настроен.
                  Полная шахматка — во вкладке «Брони».
                </p>
                <div className="row" style={{ justifyContent: 'flex-end' }}>
                  <button className="btn accent" onClick={() => setBookFor(null)}>Готово</button>
                </div>
              </>
            ) : (
              <>
                <h2>Бронь: {bookFor.name ?? formatPhoneKz(bookFor.phone)}</h2>
                {tables.length === 0 && <p className="dlg-line">Столов пока нет — расставьте их во вкладке «Зал».</p>}
                {tables.length > 0 && (
                  <>
                    <label>Стол
                      <select value={bk.tableId} onChange={(e) => setBk({ ...bk, tableId: e.target.value })}>
                        <option value="">— выберите —</option>
                        {tables.map((t) => <option key={t.id} value={t.id}>{t.hall} · {t.name} ({t.seats})</option>)}
                      </select>
                    </label>
                    <div className="row">
                      <label>Дата <input type="date" value={bk.date}
                        onChange={(e) => setBk({ ...bk, date: e.target.value })} /></label>
                      <label>Время <input type="time" value={bk.time}
                        onChange={(e) => setBk({ ...bk, time: e.target.value })} /></label>
                      <label>Часа
                        <select value={bk.durationMin} onChange={(e) => setBk({ ...bk, durationMin: e.target.value })}>
                          <option value="60">1</option><option value="90">1.5</option>
                          <option value="120">2</option><option value="180">3</option>
                        </select>
                      </label>
                    </div>
                    {createResv.isError && (createResv.error as Error).message === 'TABLE_BUSY' && (
                      <p className="dlg-err">Стол в это время уже занят — выберите другой или сдвиньте время.</p>
                    )}
                    <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
                      <button className="btn" onClick={() => setBookFor(null)}>Отмена</button>
                      <button className="btn accent" disabled={!bk.tableId || createResv.isPending}
                        onClick={() => createResv.mutate({
                          locationId: loc,
                          tableId: bk.tableId,
                          guestName: bookFor.name ?? formatPhoneKz(bookFor.phone),
                          phone: bookFor.phone,
                          guests: Math.max(1, Number(bk.guests) || 2),
                          startAt: new Date(`${bk.date}T${bk.time}:00`).toISOString(),
                          durationMin: Number(bk.durationMin),
                        })}>
                        Создать
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
      {sheet}
      {noteView}
    </div>
  );
}

function ReferralBox({ token }: { token: string }) {
  const [cfg, setCfg] = useState<{ everyVisits: string; percent: string; on: boolean } | null>(null);
  useEffect(() => {
    void fetch(`${API}/guests/referral`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => {
        const d = (await r.json()) as { referral: { everyVisits: number; percent: number } | null };
        setCfg(d.referral
          ? { everyVisits: String(d.referral.everyVisits), percent: String(d.referral.percent), on: true }
          : { everyVisits: '5', percent: '10', on: false });
      })
      .catch(() => setCfg({ everyVisits: '5', percent: '10', on: false }));
  }, [token]);
  if (!cfg) return null;
  const save = (next: NonNullable<typeof cfg>) => {
    setCfg(next);
    void fetch(`${API}/guests/referral`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(next.on
        ? { everyVisits: Number(next.everyVisits) || 5, percent: Number(next.percent) || 10 }
        : null),
    });
  };
  return (
    <div className="loyalty-box" style={{ marginBottom: 12, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
      <label className="row" style={{ gap: 8 }}>
        <input type="checkbox" checked={cfg.on}
          onChange={(e) => save({ ...cfg, on: e.target.checked })} />
        <b>«Приведи друга»</b>
      </label>
      <PromoStats token={token} />
      {cfg.on && (
        <span className="row" style={{ gap: 6, alignItems: 'center' }}>
          каждые
          <input style={{ width: 52 }} inputMode="numeric" value={cfg.everyVisits}
            onChange={(e) => save({ ...cfg, everyVisits: e.target.value.replace(/\D/g, '') })} />
          визитов — код на
          <input style={{ width: 52 }} inputMode="numeric" value={cfg.percent}
            onChange={(e) => save({ ...cfg, percent: e.target.value.replace(/\D/g, '') })} />
          % другу (на чеке, 30 дней)
        </span>
      )}
    </div>
  );
}


function PromoStats({ token }: { token: string }) {
  const [s, setS] = useState<{ issued: number; redeemed: number; expired: number;
    discountGiven: number; revenueFromRedeemed: number } | null>(null);
  useEffect(() => {
    void fetch(`${API}/guests/referral-stats`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => setS(await r.json()))
      .catch(() => undefined);
  }, [token]);
  if (!s || s.issued + s.redeemed === 0) return null;
  const tg2 = (v: number) => `${(v / 100).toLocaleString('ru-RU')} ₸`;
  return (
    <span className="hint-inline" style={{ display: 'block', width: '100%' }}>
      За 30 дней: выдано {s.issued} · погашено {s.redeemed}
      {s.expired > 0 && <> · сгорело {s.expired}</>} · скидок {tg2(s.discountGiven)}
      {s.revenueFromRedeemed > 0 && <> · чеков с кодами на <b>{tg2(s.revenueFromRedeemed)}</b></>}
    </span>
  );
}


function FeedbackBox({ token }: { token: string }) {
  const [d, setD] = useState<{ avg: number | null; count: number;
    rows: { rating: number; comment: string | null; at: string }[] } | null>(null);
  useEffect(() => {
    void fetch(`${API}/guests/feedback-recent`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => setD(await r.json())).catch(() => undefined);
  }, [token]);
  if (!d || d.count === 0) return null;
  return (
    <div className="loyalty-box" style={{ marginBottom: 12 }}>
      <b>Отзывы за 7 дней:</b> ★{d.avg?.toFixed(1)} <span className="hint-inline">({d.count})</span>
      {d.rows.filter((x) => x.comment).slice(0, 3).map((x, i) => (
        <span key={i} className="hint-inline" style={{ display: 'block' }}>
          {'★'.repeat(x.rating)} «{x.comment}»
        </span>
      ))}
    </div>
  );
}
